import type { PostContent } from '@/types/board';

/**
 * TipTap 에디터의 content를 안전하게 파싱하는 함수
 * 
 * 문자열로 저장된 TipTap JSON을 PostContent 객체로 변환합니다.
 * 파싱 실패 시 기본 문서 구조를 반환하여 애플리케이션 오류를 방지합니다.
 * 
 * @param content - 파싱할 content (문자열 또는 객체)
 * @returns 파싱된 PostContent 객체
 * 
 * @example
 * ```typescript
 * const parsedContent = parseContent(contentString);
 * if (parsedContent.content.length > 0) {
 *   console.log('파싱된 콘텐츠:', parsedContent);
 * }
 * ```
 */
export function parseContent(content: string): PostContent {
  try {
    const parsed = JSON.parse(content);
    // TipTap 문서 구조 검증
    if (parsed.type === 'doc' && parsed.content) {
      return parsed;
    }
    // 유효하지 않은 구조인 경우 기본 문서 반환
    return { type: 'doc', content: [] };
  } catch (_parseError) {
    // JSON 파싱 실패 시 기본 문서 반환
    return { type: 'doc', content: [] };
  }
}

/**
 * PostContent 객체를 JSON 문자열로 변환하는 함수
 * 
 * TipTap 에디터에서 사용할 수 있도록 PostContent를 문자열로 직렬화합니다.
 * 변환 실패 시 기본 문서 구조의 JSON을 반환합니다.
 * 
 * @param content - 변환할 PostContent 객체
 * @returns JSON 문자열
 * 
 * @example
 * ```typescript
 * const jsonString = stringifyContent(postContent);
 * localStorage.setItem('draft', jsonString);
 * ```
 */
export function stringifyContent(content: PostContent): string {
  try {
    return JSON.stringify(content);
  } catch (error) {
    // 직렬화 실패 시 기본 문서 구조 반환
    return JSON.stringify({ type: 'doc', content: [] });
  }
}

/**
 * 글 내용의 임시 경로를 영구 경로로 변경하는 함수
 * 
 * 사용자가 글 작성 중에 업로드한 임시 이미지나 첨부파일의 경로를
 * 글 저장 후 영구 경로로 변경합니다. Vercel Blob URL은 이미 영구 URL이므로
 * 변경하지 않습니다.
 * 
 * @param content - 변경할 글 내용 (PostContent)
 * @returns 경로가 변경된 글 내용 (PostContent)
 * 
 * @example
 * ```typescript
 * const permanentContent = convertTempPathsToPermanent(draftContent);
 * await savePost(permanentContent);
 * ```
 */
export const convertTempPathsToPermanent = (content: PostContent): PostContent => {
  const contentString = JSON.stringify(content);

  // Vercel Blob URL인 경우 처리하지 않음 (이미 영구 URL)
  if (contentString.includes('blob.vercel-storage.com')) {
    return content;
  }

  // 로컬 파일 시스템의 임시 경로를 영구 경로로 변경
  let updatedContent = contentString.replace(
    /uploads\/temp\/([^"'\s]+)/g,
    'uploads/images/permanent_$1'
  );

  // 임시 첨부파일 경로를 영구 경로로 변경
  updatedContent = updatedContent.replace(
    /uploads\/temp-attachments\/([^"'\s]+)/g,
    'uploads/attachments/permanent_$1'
  );

  return JSON.parse(updatedContent);
};

/**
 * 임시 이미지 경로를 영구 경로로 변환하는 함수 (새로운 구조)
 * 
 * 새로운 파일 업로드 구조에 맞춰 임시 이미지 경로를 영구 경로로 변환합니다.
 * Vercel Blob URL은 변환하지 않으며, 로컬 임시 경로만 처리합니다.
 * 
 * @param content - 변환할 HTML 콘텐츠 문자열
 * @param postId - 게시글 ID (현재 사용되지 않음, 향후 확장성 고려)
 * @param postType - 게시글 타입 (현재 사용되지 않음, 향후 확장성 고려)
 * @returns 경로가 변환된 HTML 콘텐츠 문자열
 * 
 * @example
 * ```typescript
 * const permanentContent = convertTempImagePathsToPermanent(htmlContent, '123', 'notice');
 * await updatePost(postId, permanentContent);
 * ```
 */
export function convertTempImagePathsToPermanent(content: string, postId: string, postType: string): string {
  // Vercel Blob URL인 경우 변환하지 않음
  if (content.includes('blob.vercel-storage.com')) {
    return content;
  }

  // 임시 이미지 경로를 영구 경로로 변환
  return content.replace(
    /\/uploads\/temp\/([^"'\s>]+)/g,
    `/uploads/images/$1`
  );
}

/**
 * 팝업 이미지를 영구 경로로 변환하는 함수 (새로운 구조)
 * 
 * 팝업 관리에서 사용하는 이미지의 임시 경로를 영구 경로로 변환합니다.
 * Vercel Blob URL은 변환하지 않으며, 로컬 임시 경로만 처리합니다.
 * 
 * @param imageUrl - 변환할 이미지 URL
 * @returns 경로가 변환된 이미지 URL
 * 
 * @example
 * ```typescript
 * const permanentImageUrl = convertPopupImageToPermanent(tempImageUrl);
 * await savePopup({ ...popupData, imageUrl: permanentImageUrl });
 * ```
 */
export function convertPopupImageToPermanent(imageUrl: string): string {
  // Vercel Blob URL인 경우 변환하지 않음
  if (imageUrl.includes('blob.vercel-storage.com')) {
    return imageUrl;
  }

  // 임시 경로를 영구 경로로 변환
  if (imageUrl.startsWith('/uploads/temp/')) {
    return imageUrl.replace('/uploads/temp/', '/uploads/images/');
  }

  return imageUrl;
} 